import { useQuery } from "@apollo/client";
import { Autocomplete, Chip, FormControl, TextField } from "@mui/material";
import { gql } from "generated-graphql";

const TENANT_WASTE_VENDORS_QUERY = gql(`
  query TenantWasteVendors($tenantId: ID!) {
    tenantWasteVendors(tenantId: $tenantId)
  }
`);

type WasteVendorPickerProps = {
  /** Scopes the vendor option list to a single tenant. */
  tenantId?: string;
  label?: string;
  disabled?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  value: string[];
  onChange: (value: string[]) => void;
};

/**
 * Selects multiple vendors from the tenant's waste manifests.
 * Autocomplete filters the distinct vendor names client-side.
 */
export default function WasteVendorPicker(props: WasteVendorPickerProps) {
  const { tenantId, value, onChange, label, disabled, onOpen, onClose } = props;
  const { data, loading } = useQuery(TENANT_WASTE_VENDORS_QUERY, {
    variables: { tenantId: tenantId ?? "" },
    skip: !tenantId,
  });
  const options = data?.tenantWasteVendors ?? [];

  return (
    <FormControl fullWidth>
      <Autocomplete
        multiple
        size="small"
        options={options}
        value={value}
        loading={loading}
        disabled={disabled}
        onChange={(_, newValue) => onChange(newValue)}
        onOpen={onOpen}
        onClose={onClose}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={!value.length ? "Select..." : ""}
            autoComplete="off"
            inputProps={{
              ...params.inputProps,
              autoComplete: "off",
              "data-lpignore": "true",
              "data-form-type": "other",
            }}
          />
        )}
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => {
            const { key, ...chipProps } = getTagProps({ index });
            return <Chip key={key} label={option} size="small" {...chipProps} />;
          })
        }
      />
    </FormControl>
  );
}
